import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { ICompanyInformation } from '@static/models/company';
import { HttpService } from '@static/services/http.service';
import { SnackService } from '@static/services/snack.service';
import { ToastService } from '@static/services/toast.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private _httpService: HttpService = inject(HttpService);
  private _snackService: SnackService = inject(SnackService);
  private _toastService: ToastService = inject(ToastService);

  public companyList: ICompanyInformation[];
  public companyListUpdated: BehaviorSubject<ICompanyInformation[]>;

  constructor() {
    this.companyList = [];
    this.companyListUpdated = new BehaviorSubject<ICompanyInformation[]>(this.companyList);
  }

  /**
   * Update the company information
   *
   * @param company company info
   */
  public updateCompany(company: ICompanyInformation): void {
    this._httpService.postP(`${environment.backendUri}/admin/company`, company)
      .then(() => {
        this.getCompanyList();
        this._snackService.openInfoSnack("Company updated");
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) this._snackService.openErrorSnack('Insufficient permissions', err);
        else this._snackService.openErrorSnack('Failed to update company', err)
      });
  }

  /**
   * Delete the company record
   *
   * @param id  company record to delete
   */
  public deleteCompany(id: number): void {
    this._httpService.deleteP(`${environment.backendUri}/admin/company/${id}`)
      .then(() => {
        this.companyList = this.companyList.filter(c => c.id !== id);
        this.companyListUpdated.next(this.companyList);
        this._snackService.openInfoSnack("Company deleted");
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) this._snackService.openErrorSnack('Insufficient permissions', err);
        else this._snackService.openErrorSnack('Failed to delete company', err)
      });
  }

  /**
   * Get company list
   */
  public getCompanyList(): void {
    const toastId = this._toastService.addToast('Loading Company Data', undefined, true);
    this._httpService.getP<ICompanyInformation[]>(`${environment.backendUri}/admin/company`)
      .then((data) => {
        this.companyList = data;
        this.companyListUpdated.next(this.companyList);
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) this._snackService.openErrorSnack('Insufficient permissions', err);
        else this._snackService.openErrorSnack('Failed to get company list', err)
      })
      .finally(() => this._toastService.removeToast(toastId));
  }
}
